import { useTranslation } from "react-i18next";
import { UNIT_STATUS_COLORS } from "../../../../constants/unitColors";

interface StatusFiltersProps {
  selectedStatus: string | null;
  onStatusClick: (status: string) => void;
  onClear: () => void;
  unitStats: {
    driving: number;
    ralenti: number;
    stopped: number;
    offline: number;
    total: number;
  };
  themeClasses: {
    cardBorder: string;
    text: string;
    textSecondary: string;
    buttonHover: string;
  };
}

// Una sola fila de pestañas, no una cuadrícula de tarjetas. La cuadrícula 2x2
// ocupaba ~140 px del panel y, sumada al resto del encabezado, dejaba ver 1.5
// unidades en una pantalla de 761 px de alto (medido el 5-oct-2026); Mapon y
// Wialon muestran entre 4 y 20 en el mismo espacio. Además el número es el
// filtro: la pestaña "Todos" reemplaza al botón "Limpiar filtro".
export default function StatusFilters({
  selectedStatus,
  onStatusClick,
  onClear,
  unitStats,
  themeClasses,
}: StatusFiltersProps) {
  const { t } = useTranslation("lite");

  const statuses = [
    {
      id: null,
      label: t("status.all"),
      count: unitStats.total,
      color: null,
      animate: false,
    },
    {
      id: "driving",
      label: t("status.driving"),
      count: unitStats.driving,
      color: UNIT_STATUS_COLORS.driving,
      animate: true,
    },
    {
      id: "ralenti",
      label: t("status.ralenti"),
      count: unitStats.ralenti,
      color: UNIT_STATUS_COLORS.ralenti,
      animate: false,
    },
    {
      id: "stopped",
      label: t("status.stopped"),
      count: unitStats.stopped,
      color: UNIT_STATUS_COLORS.stopped,
      animate: false,
    },
    {
      id: "offline",
      label: t("status.offline"),
      count: unitStats.offline,
      color: UNIT_STATUS_COLORS.offline,
      animate: false,
    },
  ];

  return (
    <div
      role="tablist"
      className={`flex mb-3 border-b ${themeClasses.cardBorder}`}
    >
      {statuses.map((status) => {
        const isSelected = selectedStatus === status.id;
        return (
          <button
            key={status.id ?? "all"}
            type="button"
            role="tab"
            aria-selected={isSelected}
            title={`${status.label}: ${status.count}`}
            onClick={() =>
              status.id === null ? onClear() : onStatusClick(status.id)
            }
            className={`flex-1 min-w-0 -mb-px px-1 pt-1.5 pb-2 border-b-2 rounded-t-md transition-colors active:translate-y-px ${themeClasses.buttonHover} ${
              isSelected ? themeClasses.text : `border-transparent ${themeClasses.textSecondary}`
            }`}
            style={
              isSelected
                ? { borderColor: status.color ?? "currentColor" }
                : undefined
            }
          >
            <span
              className={`flex items-center justify-center gap-1.5 text-xl leading-none tabular-nums ${
                isSelected ? "font-bold" : `font-semibold ${themeClasses.text}`
              }`}
            >
              {status.color && (
                <span
                  className={`w-1.5 h-1.5 rounded-full shrink-0 ${
                    status.animate ? "animate-pulse" : ""
                  }`}
                  style={{ backgroundColor: status.color }}
                />
              )}
              {status.count}
            </span>
            <span className="mt-1 block truncate text-[11px] leading-tight">
              {status.label}
            </span>
          </button>
        );
      })}
    </div>
  );
}
